// src/pages/MicroCreditPage.jsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Container, Table, Button, InputGroup, Form, Pagination, Row, Col, Badge, Alert
} from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import ProtectedSection from '../components/ProtectedSection';
import microCreditService from '../services/microCreditService';
import MicroCreditModal from '../components/MicroCreditModal';
import MicroCreditViewModal from '../components/MicroCreditViewModal';
import MicroCreditTransactionModal from '../components/MicroCreditTransactionModal';

const badgeVariant = (status) => {
  switch (String(status || '').toUpperCase()) {
    case 'PENDING': return 'secondary';
    case 'CONFIRMED': return 'info';
    case 'NOT_CONFIRMED': return 'dark';
    case 'IN_PROGRESS': return 'primary';
    case 'OVERDUE': return 'warning';
    case 'CLOSED': return 'success';
    default: return 'secondary';
  }
};

export default function MicroCreditPage() {
  const { hasRight } = useContext(AuthContext);

  const canView = hasRight('MICRO_CREDIT_VIEW');
  const canCreate = hasRight('MICRO_CREDIT_CREATE');
  const canUpdate = hasRight('MICRO_CREDIT_UPDATE');
  const canDelete = hasRight('MICRO_CREDIT_DELETE');
  const canConfirm = hasRight('MICRO_CREDIT_CONFIRM');

  const [rows, setRows] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [viewModalShow, setViewModalShow] = useState(false);
  const [viewId, setViewId] = useState(null);

  const [txModalShow, setTxModalShow] = useState(false);
  const [txId, setTxId] = useState(null);

  const fetchAll = () => {
    if (!canView) return;
    setErrorMsg('');

    microCreditService
      .list({
        page,
        pageSize,
        search: filter,
        status: statusFilter,
        currency: currencyFilter,
      })
      .then((r) => {
        setRows(r?.data || []);
        setTotal(r?.total || 0);
      })
      .catch((e) => {
        setErrorMsg(e?.response?.data?.message || 'Erreur chargement Micro Crédit.');
      });
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, page, pageSize, filter, statusFilter, currencyFilter]);

  useEffect(() => {
    setPage(1);
  }, [filter, statusFilter, currencyFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const paginationItems = useMemo(() => {
    const items = [];
    items.push(
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
    );

    const from = Math.max(1, page - 2);
    const to = Math.min(totalPages, page + 2);

    if (from > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = from; p <= to; p++) {
      items.push(
        <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
          {p}
        </Pagination.Item>
      );
    }
    if (to < totalPages) items.push(<Pagination.Ellipsis key="e2" disabled />);

    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );

    return items;
  }, [page, totalPages]);

  const openCreate = () => {
    setEditRow(null);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setShowModal(true);
  };

  const openView = (row) => {
    setViewId(row.id);
    setViewModalShow(true);
  };

  const openTransactions = (row) => {
    setTxId(row.id);
    setTxModalShow(true);
  };

  const save = (payload) => {
    setErrorMsg('');
    const op = editRow?.id
      ? microCreditService.update(editRow.id, payload)
      : microCreditService.create(payload);

    op.then(() => {
      setShowModal(false);
      setEditRow(null);
      fetchAll();
    }).catch((e) => {
      setErrorMsg(e?.response?.data?.message || 'Erreur enregistrement Micro Crédit.');
    });
  };

  const confirmRow = (row) => {
    if (!window.confirm('Confirmer ce Micro Crédit ?')) return;
    setErrorMsg('');
    microCreditService.confirm(row.id)
      .then(fetchAll)
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur confirmation.'));
  };

  const notConfirmRow = (row) => {
    if (!window.confirm('Marquer ce Micro Crédit comme non confirmé ?')) return;
    setErrorMsg('');
    microCreditService.notConfirm(row.id)
      .then(fetchAll)
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur action non confirmé.'));
  };

  const removeRow = (row) => {
    if (!window.confirm('Supprimer ce Micro Crédit ?')) return;
    setErrorMsg('');
    microCreditService.remove(row.id)
      .then(fetchAll)
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur suppression.'));
  };

  const canOpenTransactions = (row) =>
    ['CONFIRMED', 'IN_PROGRESS', 'OVERDUE', 'CLOSED'].includes(String(row.status || '').toUpperCase());

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>Micro Crédit</h2>
        <ProtectedSection code="MICRO_CREDIT_CREATE">
          <Button onClick={openCreate}>+ Ajouter un Micro Crédit</Button>
        </ProtectedSection>
      </div>

      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      <Row className="g-2 mb-3">
        <Col md={5}>
          <InputGroup>
            <Form.Control
              placeholder="Recherche (client, compte, devise, statut...)"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <Button variant="outline-secondary" onClick={() => setFilter('')}>×</Button>
          </InputGroup>
        </Col>

        <Col md={3}>
          <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Tous statuts</option>
            <option value="PENDING">Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="NOT_CONFIRMED">Not confirmed</option>
            <option value="IN_PROGRESS">En cours</option>
            <option value="OVERDUE">En retard</option>
            <option value="CLOSED">Clôturé</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)}>
            <option value="">Toutes devises</option>
            <option value="HTG">HTG</option>
            <option value="USD">USD</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </Form.Select>
        </Col>
      </Row>

      <ProtectedSection code="MICRO_CREDIT_VIEW">
        <Table hover responsive>
          <thead>
            <tr>
              <th>Client</th>
              <th>N° Compte</th>
              <th>Devise</th>
              <th className="text-end">Montant prêté</th>
              <th className="text-end">Frais</th>
              <th className="text-end">Total à encaisser</th>
              <th className="text-end">Déjà encaissé</th>
              <th className="text-end">Reste</th>
              <th>Date fin</th>
              <th>Statut</th>
              <th style={{ width: 420 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const clientName = `${r.client?.firstName || ''} ${r.client?.lastName || ''}`.trim() || '—';
              const status = String(r.status || '').toUpperCase();

              return (
                <tr key={r.id}>
                  <td>{clientName}</td>
                  <td>{r.accountNumber}</td>
                  <td>{r.currency}</td>
                  <td className="text-end">{Number(r.principalAmount || 0).toFixed(2)}</td>
                  <td className="text-end">{Number(r.feeAmount || 0).toFixed(2)}</td>
                  <td className="text-end">{Number(r.totalRepayable || 0).toFixed(2)}</td>
                  <td className="text-end">{Number(r.totalCollected || 0).toFixed(2)}</td>
                  <td className="text-end">{Number(r.remainingAmount || 0).toFixed(2)}</td>
                  <td>{r.endDate ? new Date(r.endDate).toLocaleDateString() : '—'}</td>
                  <td><Badge bg={badgeVariant(status)}>{r.status}</Badge></td>
                  <td>
                    <ProtectedSection code="MICRO_CREDIT_VIEW">
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-2 mb-1"
                        onClick={() => openView(r)}
                      >
                        Voir
                      </Button>
                    </ProtectedSection>

                    <ProtectedSection code="MICRO_CREDIT_CONFIRM">
                      {['PENDING', 'NOT_CONFIRMED'].includes(status) && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => confirmRow(r)}
                        >
                          Confirm
                        </Button>
                      )}

                      {['PENDING', 'CONFIRMED'].includes(status) && !r.hasTransactions && (
                        <Button
                          variant="outline-dark"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => notConfirmRow(r)}
                        >
                          Not confirm
                        </Button>
                      )}
                    </ProtectedSection>

                    <ProtectedSection code="MICRO_CREDIT_UPDATE">
                      {!['IN_PROGRESS', 'OVERDUE', 'CLOSED'].includes(status) && !r.hasTransactions && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => openEdit(r)}
                        >
                          Modifier
                        </Button>
                      )}
                    </ProtectedSection>

                    <ProtectedSection code="MICRO_CREDIT_DELETE">
                      {!r.hasTransactions && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => removeRow(r)}
                        >
                          Supprimer
                        </Button>
                      )}
                    </ProtectedSection>

                    <ProtectedSection code="MICRO_CREDIT_VIEW">
                      {canOpenTransactions(r) && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mb-1"
                          onClick={() => openTransactions(r)}
                        >
                          Transactions
                        </Button>
                      )}
                    </ProtectedSection>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        {rows.length === 0 && (
          <p className="text-center text-muted">Aucun Micro Crédit trouvé.</p>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted">
            Résultats : {total} • Page {page}/{totalPages}
          </div>
          <Pagination className="mb-0">{paginationItems}</Pagination>
        </div>
      </ProtectedSection>

      <MicroCreditModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditRow(null);
        }}
        onSave={save}
        initialData={editRow}
      />

      <MicroCreditViewModal
        show={viewModalShow}
        onHide={() => setViewModalShow(false)}
        microCreditId={viewId}
      />

      <MicroCreditTransactionModal
        show={txModalShow}
        onHide={() => {
          setTxModalShow(false);
          fetchAll();
        }}
        microCreditId={txId}
      />
    </Container>
  );
}
